import {MultiSelect} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";
import {Skeleton} from "primereact/skeleton";
import {
    addSkillGroup,
    addSkillGroupGarbage,
    addUser,
    changeCampaignUserLevel,
    changeSkillGroupGarbageIndex,
    changeSkillGroupLevel,
    deleteSkillGroup,
    deleteSkillGroupGarbage,
    deleteUser,
    setChangedCompaniesData,
    setCompanyField,
    setEditType,
} from "redux/actions/actions";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import React, {useRef, useState} from "react";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {classNames} from "primereact/utils";
import {InputSwitch} from "primereact/inputswitch";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {PROD} from "../../../redux/config";
import {getUserDetails} from "../../../redux/api/apiUsersList";
import {checkPermissions} from "../../../redux/permissions/permissionsUtils";
import UserListDialog from "../../Settings/UsersList/UserDialod";
import {getQualificationGroupDetails} from "../../../redux/api/apiQualificationGroup";
import QualificationGroupDialog from "../../QualificationGroupList/QualificationGroupDialog";

const CompaniesListDialogUsers = () => {
    const dispatch = useAppDispatch()
    const companiesData = useAppSelector(state => state.CompaniesListReducer.companiesData)
    const usersData = useAppSelector(state => state.CompaniesListReducer.usersData);
    const skillGroupList = useAppSelector(state => state.QualificationGroupListReducer.skillGroupList)
    const companiesDirectoryCampaignStrategy = useAppSelector(state => state.CompaniesListReducer.companiesDirectoryCampaignStrategy)
    const selectedCompanyId = useAppSelector(state => state.CompaniesListReducer.selectedCompanyId)
    const loadingStrategyDict = useAppSelector(state => state.CompaniesListReducer.loadingStrategyDict)
    const archiveTableToggle = useAppSelector(state => state.CompaniesListReducer.archiveTableToggle)
    const statusDone = companiesData?.campaign?.statusId === 6;
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const inputRef = useRef<any>(null);
    const errorFields = useAppSelector(state => state.CompaniesListReducer.errorFields);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const usersManagerPermissions = checkPermissions(rolesVision, 'Users');
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const detailsLoading = useAppSelector(state => state.UsersListReducer.detailLoading);
    const campaignDetailsLoading = useAppSelector(state => state.CompaniesListReducer.detailsLoading);
    const skillGroupDetailsLoading = useAppSelector(state => state.QualificationGroupListReducer.detailsLoading);
    const skillGroupPermissions = checkPermissions(rolesVision, 'SkillGroup');
    const [currentUserId, setCurrentUserId] = useState<any>(null);

    const {
        update: hasUsersManagerUpdatePermission,
        visible: hasUsersManagerVisiblePermission
    } = usersManagerPermissions;

    const {
        visible: hasRoleVisionPermission,
        update: hasRoleUpdatePermission,
    } = skillGroupPermissions;

    const sameSkillGroupId = (a: any, b: any) =>
        a != null && b != null && String(a) === String(b);

    const skillGroupRowId = (row: any) => row?.id ?? row?.skillGroupId;

    const usersIsBlock = companiesData?.permission?.usersIsBlock;
    const skillGroupsIsBlock = companiesData?.permission?.skillGroupsIsBlock;

    const isSkillGroupInCatalog = (skillGroupId: any) =>
        Boolean(
            skillGroupId != null &&
            skillGroupList?.some((sg: any) => sameSkillGroupId(skillGroupRowId(sg), skillGroupId))
        );

    const canOpenSkillGroupDetails = (skillGroupId: any) => {
        if (campaignDetailsLoading || skillGroupsIsBlock || !hasRoleVisionPermission) {
            return false;
        }
        return isSkillGroupInCatalog(skillGroupId);
    };

    const canEditSkillGroupDetails = (skillGroupId: any) =>
        Boolean(archiveTableToggle && hasRoleUpdatePermission && isSkillGroupInCatalog(skillGroupId));

    const skillGroupDetailsIcon = (skillGroupId: any) =>
        canEditSkillGroupDetails(skillGroupId) ? 'pi pi-pencil' : 'pi pi-eye';

    const isUserInCatalog = (userId: any) =>
        Boolean(
            userId != null &&
            usersData?.some((user: any) => String(user.userId) === String(userId))
        );

    const mergePreservedSelectedItems = (
        selected: any[],
        existing: any[],
        getId: (item: any) => any,
        isInCatalog: (id: any) => boolean,
    ) => {
        const selectedIds = new Set(
            (selected || [])
                .map(getId)
                .filter((id: any) => id != null)
                .map((id: any) => String(id))
        );
        const result: any[] = [];
        const seen = new Set<string>();

        (existing || []).forEach((item: any) => {
            const id = getId(item);
            if (id == null) {
                return;
            }
            const key = String(id);
            const keep = !isInCatalog(id) || selectedIds.has(key);
            if (keep && !seen.has(key)) {
                seen.add(key);
                result.push(item);
            }
        });

        (selected || []).forEach((item: any) => {
            const id = getId(item);
            if (id == null) {
                return;
            }
            const key = String(id);
            if (!seen.has(key)) {
                seen.add(key);
                result.push(item);
            }
        });

        return result;
    };

    const canOpenUserDetails = (userId: any) => {
        if (campaignDetailsLoading || usersIsBlock || !hasUsersManagerVisiblePermission || userId == null) {
            return false;
        }
        return isUserInCatalog(userId);
    };

    const canEditUserDetails = (userId: any) =>
        Boolean(archiveTableToggle && hasUsersManagerUpdatePermission && isUserInCatalog(userId));

    const userDetailsIcon = (userId: any) =>
        canEditUserDetails(userId) ? 'pi pi-pencil' : 'pi pi-eye';

    const buildSkillGroupOptions = (existingGroups: any[], withLevel = false) => {
        const campaignId = companiesData?.campaign?.campaignId;
        const fromDict: any[] = (skillGroupList || []).map(({isActive, ...data}: any) => {
            const gid = data.id ?? data.skillGroupId;
            return {
                ...data,
                id: gid,
                skillGroupId: gid,
                ...(withLevel ? {skillGroupLevel: 0} : {}),
                ...(campaignId ? {campaignId} : {}),
            };
        });
        const byId = new Map<string, any>(fromDict.map((sg: any) => [String(sg.skillGroupId ?? sg.id), sg]));

        (existingGroups || []).forEach((existing: any) => {
            const gid = existing.skillGroupId ?? existing.id;
            if (gid == null) {
                return;
            }
            const key = String(gid);
            const fromCatalog = byId.get(key);
            if (fromCatalog) {
                byId.set(key, {
                    ...fromCatalog,
                    ...existing,
                    skillGroupId: gid,
                    id: gid,
                    name: fromCatalog.name || existing.name || '',
                    ...(withLevel
                        ? {skillGroupLevel: existing.skillGroupLevel ?? fromCatalog.skillGroupLevel ?? 0}
                        : {}),
                    ...(campaignId ? {campaignId: existing.campaignId ?? campaignId} : {}),
                });
            } else {
                byId.set(key, {
                    ...existing,
                    skillGroupId: gid,
                    id: gid,
                    name: existing.name || '',
                    ...(withLevel ? {skillGroupLevel: existing.skillGroupLevel ?? 0} : {}),
                    ...(campaignId ? {campaignId: existing.campaignId ?? campaignId} : {}),
                });
            }
        });

        return Array.from(byId.values());
    };

    const skillGroupDataWithLevel = buildSkillGroupOptions(companiesData?.skillGroups, true);
    const garbageUtilizationGroupOptions = buildSkillGroupOptions(companiesData?.skillGroupsGarbage);

    const utilizationGarbageGroupSelected = (() => {
        const mapped = skillGroupsIsBlock
            ? (companiesData?.skillGroupsGarbage || []).map((row: any) => ({
                ...row,
                id: row.skillGroupId,
                skillGroupId: row.skillGroupId,
                name: row.name,
                displayOrder: row.displayOrder ?? row.skillGroupLevel ?? 1,
            }))
            : (companiesData?.skillGroupsGarbage || []).map((row: any) => {
                const skillGroupId = row.skillGroupId ?? row.id;
                const fromList = skillGroupList?.find((item: any) =>
                    sameSkillGroupId(item.id, skillGroupId)
                );
                return {
                    ...row,
                    skillGroupId,
                    id: skillGroupId,
                    name: row.name || fromList?.name || '',
                    displayOrder: row.displayOrder ?? row.skillGroupLevel ?? 1,
                };
            });

        const byId = new Map<string, any>();
        mapped.forEach((item: any) => {
            if (item.skillGroupId == null) {
                return;
            }
            const key = String(item.skillGroupId);
            if (!byId.has(key)) {
                byId.set(key, item);
            }
        });
        return Array.from(byId.values());
    })();

    const resolveGarbageSkillGroupName = (row: any) => {
        if (row?.name) {
            return row.name;
        }
        const sg = skillGroupList?.find((s: any) =>
            sameSkillGroupId(skillGroupRowId(s), row?.skillGroupId)
        );
        return sg?.name || '';
    };

    const filteredUtilizationGarbageSkillGroups = (() => {
        const mapped = (companiesData?.skillGroupsGarbage || [])
            .map((row: any) => {
                const name = resolveGarbageSkillGroupName(row);
                if (skillGroupsIsBlock || name) {
                    return {
                        ...row,
                        skillGroupId: row.skillGroupId ?? row.id,
                        id: row.skillGroupId ?? row.id,
                        name,
                        displayOrder: row.displayOrder ?? row.skillGroupLevel ?? 1,
                    };
                }
                return null;
            })
            .filter(Boolean);

        const byId = new Map<string, any>();
        mapped.forEach((item: any) => {
            if (item.skillGroupId == null) {
                return;
            }
            const key = String(item.skillGroupId);
            if (!byId.has(key)) {
                byId.set(key, item);
            }
        });
        return Array.from(byId.values());
    })();

    const utilizationGarbageGroupDataItemTemplate = (option: any) => {
        return resolveGarbageSkillGroupName(option);
    };

    const onGarbageRowReorder = (e: {dragIndex: number; dropIndex: number; value: any[]}) => {
        const updatedRows = (e.value || []).map((row: any, index: number) => ({
            ...row,
            skillGroupId: row.skillGroupId ?? row.id,
            displayOrder: index + 1,
        }));
        dispatch(setChangedCompaniesData(true));
        dispatch(changeSkillGroupGarbageIndex(updatedRows));
    };

    const utilizationGarbageGroupItems = () => {
        const filteredArray = [...(filteredUtilizationGarbageSkillGroups || [])]
            .sort(
                (a: {displayOrder: number}, b: {displayOrder: number}) =>
                    a.displayOrder - b.displayOrder
            )
            .map((item: any, index: number) => ({
                ...item,
                skillGroupId: item.skillGroupId ?? item.id,
                name: resolveGarbageSkillGroupName(item),
                displayOrder: index + 1,
            }));

        const nameBodyTemplate = (rowData: any) => resolveGarbageSkillGroupName(rowData);

        if (skillGroupsIsBlock) {
            return filteredArray.map((row: any) => (
                <div key={row.skillGroupId} className="flex align-items-center mb-2 p-0">
                    <InputText
                        className={classNames('w-full', {'opacity-100': archiveTableToggle && editType})}
                        disabled
                        value={`${row.displayOrder} ${nameBodyTemplate(row)}`.trim()}
                    />
                </div>
            ));
        }

        const canReorder = Boolean(archiveTableToggle && editType && !statusDone);

        const buttonTemplate = (rowData: any) => (
            <Button
                name={rowData.skillGroupId}
                onClick={() => {
                    dispatch(setChangedCompaniesData(true));
                    dispatch(deleteSkillGroupGarbage(rowData.skillGroupId));
                }}
                icon="pi pi-times"
                className="delete-user p-button-secondary p-button-rounded p-button-text"
                disabled={!canReorder}
            />
        );

        const detailsBodyTemplate = (rowData: any) => {
            if (!canOpenSkillGroupDetails(rowData.skillGroupId)) {
                return null;
            }
            return (
                <Button
                    className="max-w-4rem p-button-secondary"
                    style={{minWidth: '40px'}}
                    loading={skillGroupDetailsLoading && currentUserId === rowData.skillGroupId}
                    disabled={!archiveTableToggle || skillGroupDetailsLoading}
                    icon={skillGroupDetailsIcon(rowData.skillGroupId)}
                    onClick={() => {
                        if (!canOpenSkillGroupDetails(rowData.skillGroupId)) {
                            return;
                        }
                        setCurrentUserId(rowData.skillGroupId);
                        dispatch(getQualificationGroupDetails(rowData.skillGroupId, jwtToken?.jwtToken, true));
                        dispatch(setEditType(canEditSkillGroupDetails(rowData.skillGroupId)));
                    }}
                />
            );
        };

        return (
            <DataTable
                className="w-full utilization-strategy-table"
                value={filteredArray}
                dataKey="skillGroupId"
                reorderableRows={canReorder}
                onRowReorder={onGarbageRowReorder}
            >
                <Column rowReorder style={{width: '20px'}} />
                <Column field="displayOrder" className="text-center" style={{width: '40px', minWidth: '40px'}} />
                <Column field="id" body={detailsBodyTemplate} style={{width: '40px', minWidth: '40px'}} />
                <Column field="name" body={nameBodyTemplate} style={{minWidth: '8rem'}} />
                <Column body={buttonTemplate} style={{width: '20px'}} />
            </DataTable>
        );
    };

    const usersDataWithUserLevel = (() => {
        if (usersIsBlock) {
            return (companiesData?.campaignUsers || []).map((user: any) => ({
                ...user,
                fullName: user.fullName || user.userName || '',
            }));
        }

        const fromDict = (usersData || []).map((userData: any) => ({
            ...userData,
            userLevel: 0,
            fullName: `${userData?.lastName} ${userData?.firstName} ${userData?.middleName ? ` ${userData.middleName}` : ''}`
        }));

        const byId = new Map(fromDict.map((user: any) => [String(user.userId), user]));

        (companiesData?.campaignUsers || []).forEach((campaignUser: any) => {
            const key = String(campaignUser.userId);
            const existing = byId.get(key);
            if (existing) {
                byId.set(key, {
                    ...existing,
                    userLevel: campaignUser.userLevel ?? existing.userLevel ?? 0,
                    fullName: existing.fullName?.trim()
                        ? existing.fullName
                        : (campaignUser.fullName || campaignUser.userName || existing.fullName),
                    userName: existing.userName || campaignUser.userName,
                });
            } else {
                byId.set(key, {
                    ...campaignUser,
                    userLevel: campaignUser.userLevel ?? 0,
                    fullName: campaignUser.fullName || campaignUser.userName || '',
                });
            }
        });

        return Array.from(byId.values());
    })();

    const userLevels: any = {};
    companiesData?.campaignUsers?.forEach((user: { userId: string | number; userLevel: any; }) => {
        userLevels[user.userId] = user.userLevel;
    });

    usersDataWithUserLevel?.forEach((user: { userId: string | number; userLevel: any; }) => {
        if (userLevels[user.userId] !== undefined) {
            user.userLevel = userLevels[user.userId];
        }
    });

    const filteredUsers = companiesData?.campaignUsers || [];

    const isUsersIdPresent = !!companiesData?.campaignUsers?.length;

    const userItems = companiesData?.campaignUsers?.map((campaignItem: any, index: number) => {
        const userItem = usersDataWithUserLevel?.find(
            (user: any) => String(user.userId) === String(campaignItem.userId)
        ) || campaignItem;

        const userLevelError = errorFields?.campaignUsers?.campaignUsersWithoutLevel?.find((user: { userId: any; }) => user.userId === campaignItem.userId);
        const displayName = (userItem.fullName || '').trim()
            || [userItem.lastName, userItem.firstName, userItem.middleName].filter(Boolean).join(' ')
            || userItem.userName
            || '';

        return (
            <div key={userItem.userId || index} className="flex align-items-center mb-2">
                {canOpenUserDetails(userItem.userId) && <Button
                    className="max-w-4rem p-button-secondary mr-3"
                    style={{minWidth: '40px'}}
                    loading={detailsLoading && currentUserId === userItem.userId}
                    disabled={!archiveTableToggle || detailsLoading}
                    icon={userDetailsIcon(userItem.userId)}
                    onClick={() => {
                        if (!canOpenUserDetails(userItem.userId)) {
                            return;
                        }
                        setCurrentUserId(userItem.userId);
                        dispatch(getUserDetails(userItem.userId, jwtToken?.jwtToken, true));
                        dispatch(setEditType(canEditUserDetails(userItem.userId)));
                    }}/>}
                <div className="field col-12 md:col-8 flex align-items-center m-0 p-0">
                    {displayName}
                </div>
                <InputNumber
                    ref={inputRef}
                    value={userItem?.userLevel}
                    className={classNames('', {'p-invalid': errorFields?.campaignUsers && userLevelError})}
                    disabled={!archiveTableToggle || statusDone || !editType || usersIsBlock}
                    min={0} max={1000}
                    useGrouping={false}
                    onChange={(e) => dispatch(changeCampaignUserLevel({campaignId: companiesData?.campaign?.campaignId, userId: userItem.userId, userLevel: e.value}))}
                />
                {!usersIsBlock && <Button name={userItem.userId}
                        onClick={() => {
                            dispatch(deleteUser(userItem.userId));
                        }}
                        icon="pi pi-times"
                        className="delete-user p-button-secondary p-button-rounded p-button-text field col-12 md:col-2"
                        disabled={!archiveTableToggle || statusDone || !editType}
                />}
            </div>
        )
    });

    const skillGroupUsers = (() => {
        const byOptionId = new Map<string, any>(
            (skillGroupDataWithLevel || []).map((sg: any) => [String(sg.skillGroupId ?? sg.id), sg])
        );
        const seen = new Set<string>();
        const result: any[] = [];

        (companiesData?.skillGroups || []).forEach((companiesItem: any) => {
            const skillGroupId = companiesItem.skillGroupId ?? companiesItem.id;
            if (skillGroupId == null) {
                return;
            }
            const key = String(skillGroupId);
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
            const fromOption = byOptionId.get(key);
            if (fromOption) {
                result.push(fromOption);
                return;
            }
            const fromList = skillGroupList?.find((userItem: any) =>
                sameSkillGroupId(skillGroupRowId(userItem), skillGroupId)
            );
            result.push({
                ...companiesItem,
                skillGroupId,
                id: skillGroupId,
                name: companiesItem.name || fromList?.name || '',
                skillGroupLevel: companiesItem.skillGroupLevel ?? 0,
                isActive: companiesItem.isActive ?? fromList?.isActive,
                isArchive: companiesItem.isArchive ?? fromList?.isArchive,
                campaignId: companiesData?.campaign?.campaignId ?? companiesItem.campaignId,
            });
        });
        return result;
    })();

    const filteredSkillGroups = skillGroupUsers;

    const skillGroupItems = filteredSkillGroups?.map((item: any, index: number) => {
        const fromList = skillGroupDataWithLevel?.find((sg: any) =>
            sameSkillGroupId(sg.skillGroupId ?? sg.id, item.skillGroupId)
        );
        const skillGroup = {
            ...item,
            name: item.name || fromList?.name || '',
            skillGroupLevel: item.skillGroupLevel ?? fromList?.skillGroupLevel ?? 0,
        };

        const userLevelError = errorFields?.campaignUsers?.campaignUsersWithoutLevel?.find((user: { userId: any; }) => user.userId === item.userId);

        return (
            <div key={skillGroup.skillGroupId || index} className="flex align-items-center mb-2 p-0">
                {canOpenSkillGroupDetails(skillGroup.skillGroupId) && <Button
                    className="max-w-4rem p-button-secondary mr-3"
                    style={{minWidth: '40px'}}
                    loading={skillGroupDetailsLoading && currentUserId === skillGroup.skillGroupId}
                    disabled={!archiveTableToggle || skillGroupDetailsLoading}
                    icon={skillGroupDetailsIcon(skillGroup.skillGroupId)}
                    onClick={() => {
                        if (!canOpenSkillGroupDetails(skillGroup.skillGroupId)) {
                            return;
                        }
                        setCurrentUserId(skillGroup.skillGroupId);
                        dispatch(getQualificationGroupDetails(skillGroup.skillGroupId, jwtToken?.jwtToken, true));
                        dispatch(setEditType(canEditSkillGroupDetails(skillGroup.skillGroupId)));
                    }}/>}
                <div className="field col-12 md:col-8 flex align-items-center m-0 p-0">
                    {skillGroup.name}
                </div>
                <InputNumber
                    ref={inputRef}
                    value={skillGroup?.skillGroupLevel}
                    className={classNames('', {'p-invalid': errorFields?.campaignUsers && userLevelError})}
                    disabled={!archiveTableToggle || !editType || skillGroupsIsBlock}
                    min={0} max={1000}
                    useGrouping={false}
                    onChange={(e) => dispatch(changeSkillGroupLevel({campaignId: companiesData?.campaign?.campaignId, skillGroupId: skillGroup.skillGroupId, skillGroupLevel: e.value}))}
                />
                {!skillGroupsIsBlock && <Button name={skillGroup.skillGroupId}
                        onClick={() => {
                            dispatch(deleteSkillGroup(skillGroup.skillGroupId));
                        }}
                        icon="pi pi-times"
                        className="delete-user p-button-secondary p-button-rounded p-button-text field col-12 md:col-2"
                        disabled={!archiveTableToggle || !editType}
                />}
            </div>
        )
    });

    function randomNumber(min: number, max: number) {
        return Math.floor(Math.random() * (max - min + 1) + min)
    }

    const campaignStrategyItemTemplate = (option: any) => {
        let typeName = companiesDirectoryCampaignStrategy.map((item: { dictionaryId: number; name: any; }) => {
            if (item.dictionaryId === option) {
                return item.name
            }
        })
        return (
            <div className="p-multiselect-representative-option">
                {loadingStrategyDict && <Skeleton width={`${randomNumber(50, 85)}%`} height="1rem"/>}
                {!loadingStrategyDict && <span style={{marginLeft: '.5em', verticalAlign: 'middle'}} className="image-text">
                    {typeName}
                </span>}
            </div>
        );
    }

    const selectedCampaignStrategyTemplate = (option: any) => {
        let typeName = companiesDirectoryCampaignStrategy.map((item: { dictionaryId: number; name: any; }) => {
            if (item.dictionaryId === option) {
                return item.name
            }
        })
        if (option) {
            return (
                <span style={{marginLeft: '.5em', verticalAlign: 'middle'}} className="image-text">
                    {typeName}
                </span>
            );
        }

        return "Оберіть тип";
    }

    const usersDataItemTemplate = (option: any) => {
        if (usersIsBlock || option?.fullName) {
            return option.fullName || option.userName || '';
        }
        return usersData.map((item: any) => {
            if (item.userId === option.userId) {
                return `${item.lastName} ${item.firstName} ${item?.middleName ? ` ${item.middleName}` : ''}`
            }
        })
    }

    const skillGroupDataItemTemplate = (option: any) => {
        if (option?.name) {
            return option.name;
        }
        return skillGroupList?.map((item: any) => {
            if (item.id === option.skillGroupId) {
                return item.name
            }
        })
    }


    const campaignUsers = (() => {
        const mapped = (companiesData?.campaignUsers || []).map((companiesItem: any) => {
            const user = usersDataWithUserLevel?.find(
                (userItem: any) => String(userItem.userId) === String(companiesItem.userId)
            );
            return {
                ...companiesItem,
                ...user,
                userId: companiesItem.userId,
                userLevel: companiesItem.userLevel ?? user?.userLevel ?? 0,
                userName: user?.userName || companiesItem.userName,
                fullName: (user?.fullName || companiesItem.fullName || companiesItem.userName || '').trim(),
                isActive: user?.isActive,
            };
        });

        const byId = new Map<string, any>();
        mapped.forEach((item: any) => {
            if (item.userId == null) {
                return;
            }
            const key = String(item.userId);
            if (!byId.has(key)) {
                byId.set(key, item);
            }
        });
        return Array.from(byId.values());
    })();

    const enrichSkillGroupWithName = (item: any) => {
        if (item?.name) {
            return {
                ...item,
                skillGroupId: item.skillGroupId ?? item.id,
            };
        }
        const sg = skillGroupList?.find(
            (s: any) =>
                s?.id != null &&
                (item?.skillGroupId ?? item?.id) != null &&
                String(s.id) === String(item.skillGroupId ?? item.id)
        );
        return {
            ...item,
            skillGroupId: item.skillGroupId ?? item.id ?? sg?.id,
            name: sg?.name || item?.name || '',
            isActive: item?.isActive ?? sg?.isActive,
            isArchive: item?.isArchive ?? sg?.isArchive,
        };
    };

    const addSkillGroupData = (e: any) => {
        const selected = (e.value || []).map((item: any) => {
            const skillGroupId = item?.skillGroupId ?? item?.id;
            if (skillGroupId == null) {
                return null;
            }
            return enrichSkillGroupWithName({
                ...item,
                skillGroupId,
                id: skillGroupId,
                skillGroupLevel: item.skillGroupLevel ?? 0,
            });
        }).filter(Boolean);

        const updatedEValue = mergePreservedSelectedItems(
            selected,
            companiesData?.skillGroups,
            (item: any) => item?.skillGroupId ?? item?.id,
            isSkillGroupInCatalog,
        ).map((item: any) => enrichSkillGroupWithName({
            ...item,
            skillGroupId: item.skillGroupId ?? item.id,
            skillGroupLevel: item.skillGroupLevel ?? 0,
        }));

        // @ts-ignore
        dispatch(addSkillGroup({campaignId: selectedCompanyId, skillGroupId: updatedEValue}));
    }

    const changeCampaignUsers = (e: any) => {
        const updatedEValue = mergePreservedSelectedItems(
            e.value || [],
            companiesData?.campaignUsers,
            (item: any) => item?.userId,
            isUserInCatalog,
        );

        dispatch(addUser({campaignId: selectedCompanyId, userId: updatedEValue}))
    }

    const addSkillGroupGarbageData = (e: any) => {
        const selected = (e.value || []).map((item: any) => {
            const skillGroupId = item?.skillGroupId ?? item?.id;
            if (skillGroupId == null) {
                return null;
            }
            return enrichSkillGroupWithName({
                ...item,
                skillGroupId,
                id: skillGroupId,
            });
        }).filter(Boolean);

        const updatedEValue = mergePreservedSelectedItems(
            selected,
            companiesData?.skillGroupsGarbage,
            (item: any) => item?.skillGroupId ?? item?.id,
            isSkillGroupInCatalog,
        ).map((item: any) => enrichSkillGroupWithName({
            ...item,
            skillGroupId: item.skillGroupId ?? item.id,
        }));

        dispatch(setChangedCompaniesData(true));
        dispatch(addSkillGroupGarbage({campaignId: selectedCompanyId, skillGroupId: updatedEValue}));
    }

    return (
        <div className="users">
            <div className="grid p-fluid">
                <div className="field col-12 md:col-5">
                    {/*<h6>Користувачі:</h6>*/}
                    <div className="field-radiobutton">
                        <h6 className="mr-2 mb-0">Оператори</h6>
                        <InputSwitch checked={companiesData.campaign.isSkillGroup}                             disabled={!archiveTableToggle || statusDone || !editType}
                                     onChange={(e) => {
                            dispatch(setCompanyField({section: 'campaign', fieldName: 'isSkillGroup', value: e.value}));
                        }}/>
                        <h6 className="my-0 ml-2">Група кваліфікацій</h6>
                    </div>
                    {!companiesData.campaign.isSkillGroup && <div>
                        {!usersIsBlock && <MultiSelect
                            appendTo="self"
                            value={campaignUsers}
                            options={usersDataWithUserLevel}
                            onChange={(e) => {
                                dispatch(setChangedCompaniesData(true));
                                changeCampaignUsers(e);                            }}
                            itemTemplate={usersDataItemTemplate}
                            placeholder="Оберіть користувачів"
                            className={classNames('field col-12 md:col-12 p-0', {'p-invalid': errorFields?.campaignUsers  && !campaignUsers?.length})}
                            filter
                            dataKey="userId"
                            optionLabel="fullName"
                            selectedItemsLabel={isUsersIdPresent ? `Користувачив обрано: ${filteredUsers?.length}` : ""}
                            maxSelectedLabels={0}
                            scrollHeight="250px"
                            disabled={!archiveTableToggle || statusDone || !editType}
                        />}
                        {
                            companiesData?.campaignUsers?.length && isUsersIdPresent ?
                                <div className="users-list">
                                    {userItems}
                                </div>
                                :
                                <span className="no-users-message">Користувачив не обрано</span>
                        }
                    </div>}
                    {companiesData.campaign.isSkillGroup && <div>
                        {!skillGroupsIsBlock && <MultiSelect
                            appendTo="self"
                            value={skillGroupUsers}
                            options={skillGroupDataWithLevel}
                            onChange={(e) => {
                                dispatch(setChangedCompaniesData(true));
                                addSkillGroupData(e)
                            }}
                            itemTemplate={skillGroupDataItemTemplate}
                            placeholder="Оберіть групу"
                            className={classNames('field col-12 md:col-12 p-0', {'p-invalid': errorFields?.campaignUsers  && !skillGroupUsers?.length})}
                            filter
                            dataKey="skillGroupId"
                            optionLabel="name"
                            selectedItemsLabel={skillGroupUsers?.length ? `Груп обрано: ${filteredSkillGroups?.length}` : ""}
                            maxSelectedLabels={0}
                            scrollHeight="250px"
                            disabled={!archiveTableToggle || statusDone || !editType}
                        />}
                        {
                            companiesData?.skillGroups?.length && skillGroupUsers?.length ?
                                <div className="users-list">
                                    {skillGroupItems}
                                </div>
                                :
                                <span className="no-users-message">Груп не обрано</span>
                        }
                    </div>}
                    <div className="grid col-12 md:col-12">
                        {errorFields?.campaignUsers && <div className="col-12 md:col-12 p-0 m-0 pt-2 mb-2 w-full"><span className="title p-error text-sm">{errorFields?.campaignUsers?.campaignUsers}</span></div>}
                    </div>
                </div>
                {!companiesData.campaign.IsGeneralUser && <div className="field col-12 md:col-3">
                    <h6>Стратегія оператор:</h6>
                    <Dropdown id="type"
                              appendTo="self"
                              filter
                              value={companiesData.campaign.operatorStrategyTypeId}
                              options={companiesDirectoryCampaignStrategy.map((item: { dictionaryId: number; }) => item.dictionaryId)}
                              onChange={e => {
                                  dispatch(setChangedCompaniesData(true));
                                  dispatch(setCompanyField({section: 'campaign', fieldName: 'operatorStrategyTypeId', value: e.target.value}))
                              }}
                              itemTemplate={campaignStrategyItemTemplate}
                              valueTemplate={selectedCampaignStrategyTemplate}
                              disabled={!archiveTableToggle || statusDone || !editType}
                              placeholder="Оберіть тип"
                    />
                </div>}
            </div>
            {!companiesData.campaign.IsGeneralUser && (
                <>
                    {!PROD && (
                        <div className="p-divider p-component p-divider-horizontal p-divider-solid p-divider-left"></div>
                    )}
                    <div className="grid p-fluid">
                        <div className="field col-12 md:col-12 mb-0">
                            <h5 className="mr-2 mb-0">Стратегія утилізації</h5>
                        </div>
                        <div className="grid field col-12 md:col-12">
                            <div className="field col-12 md:col-4">
                                <h6>Група кваліфікацій</h6>
                                {!skillGroupsIsBlock && <MultiSelect
                                    appendTo="self"
                                    dataKey="skillGroupId"
                                    value={utilizationGarbageGroupSelected}
                                    options={garbageUtilizationGroupOptions}
                                    onChange={(e) => {
                                        addSkillGroupGarbageData(e);
                                    }}
                                    itemTemplate={utilizationGarbageGroupDataItemTemplate}
                                    placeholder="Оберіть групу"
                                    filter
                                    optionLabel="name"
                                    selectedItemsLabel={
                                        companiesData?.skillGroupsGarbage?.length &&
                                        filteredUtilizationGarbageSkillGroups?.length
                                            ? `Груп обрано: ${filteredUtilizationGarbageSkillGroups?.length}`
                                            : ''
                                    }
                                    maxSelectedLabels={0}
                                    scrollHeight="250px"
                                    disabled={!archiveTableToggle || statusDone || !editType}
                                />}
                                {companiesData?.skillGroupsGarbage?.length &&
                                filteredUtilizationGarbageSkillGroups?.length ? (
                                    <div className="users-list">
                                        {utilizationGarbageGroupItems()}
                                        <div className="grid col-12 md:col-12">
                                            {errorFields?.campaignUsers && (
                                                <div className="col-12 md:col-12 p-0 m-0 pl-2 mb-2 w-full">
                                                    <span className="title p-error text-sm">
                                                        {errorFields?.campaignUsers?.campaignUsers}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="no-users-message">Груп не обрано</span>
                                )}
                            </div>
                            <div className="field col-12 md:col-3">
                                <h6>Стратегія оператор:</h6>
                                <Dropdown
                                    id="garbage-strategy-operators"
                                    appendTo="self"
                                    filter
                                    value={
                                        companiesData.campaign?.garbageStrategyOperators ?? 308
                                    }
                                    options={companiesDirectoryCampaignStrategy.map(
                                        (item: {dictionaryId: number}) => item.dictionaryId
                                    )}
                                    onChange={(e) => {
                                        dispatch(setChangedCompaniesData(true));
                                        dispatch(
                                            setCompanyField({
                                                section: 'campaign',
                                                fieldName: 'garbageStrategyOperators',
                                                value: e.target.value,
                                            })
                                        );
                                    }}
                                    itemTemplate={campaignStrategyItemTemplate}
                                    valueTemplate={selectedCampaignStrategyTemplate}
                                    disabled={!archiveTableToggle || statusDone || !editType}
                                    placeholder="Оберіть тип"
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
            <UserListDialog skipBranchesFetch={companiesData?.permission?.branchIsBlock}/>
            <QualificationGroupDialog skipBranchesFetch={companiesData?.permission?.branchIsBlock}/>
        </div>
    )
}

export default CompaniesListDialogUsers
